import { useCallback, useEffect, useRef } from 'react'
import {
  MessageRole,
  AgentMessage,
  StreamChunkType,
  StreamChunk,
  SendMessagePayload,
  AgentAttachmentType,
  type AgentChat,
  type AgentAttachment,
  type MultiUserMessagePayload
} from '@multiplayer-app/ai-agent-types'

import { useAgentRuntime, useAgentStore } from '@/context/AgentProvider'
import { UiChatStatus, getChatState, mapPersistedStatusToUiStatus } from '@/state/useAgentStore'

import type { ComposerFormApi } from '@/components/ChatWindow/Composer/types'

type SendResult = { type: 'success' } | { type: 'aborted' } | { type: 'error'; error: Error }

export interface UseChatSendControllerOptions {
  initialSystemPrompt?: string | ((contextKey: string) => string | undefined)
  composerFormRef: React.MutableRefObject<ComposerFormApi>
  multiUser?: boolean
  participantIds?: string[]
}

export const useChatSendController = ({
  initialSystemPrompt,
  composerFormRef,
  multiUser = false,
  participantIds = []
}: UseChatSendControllerOptions) => {
  const runtime = useAgentRuntime()
  const {
    composer,
    contextKey,
    activeChatId,
    draftChatKey,
    queuedMessages,
    activeChatStatus,
    upsertChat,
    setChatStatus,
    setActiveChat,
    setComposerError,
    moveChatKeyState,
    removeQueuedMessage,
    setPendingAssistant,
    enqueueQueuedMessage,
    appendMessagesToChat,
    commitPendingAssistant,
    setPageContextDismissed
  } = useAgentStore((state) => {
    const pendingKey = state.activeChatId ?? state.draftChatKey
    const cs = getChatState(state, pendingKey)
    return {
      composer: state.composer,
      contextKey: state.contextKey,
      activeChatId: state.activeChatId,
      draftChatKey: state.draftChatKey,
      queuedMessages: cs.messageQueue,
      activeChatStatus: cs.status,
      upsertChat: state.upsertChat,
      setActiveChat: state.setActiveChat,
      setChatStatus: state.setChatStatus,
      setComposerError: state.setComposerError,
      moveChatKeyState: state.moveChatKeyState,
      setPendingAssistant: state.setPendingAssistant,
      removeQueuedMessage: state.removeQueuedMessage,
      enqueueQueuedMessage: state.enqueueQueuedMessage,
      appendMessagesToChat: state.appendMessagesToChat,
      commitPendingAssistant: state.commitPendingAssistant,
      setPageContextDismissed: state.setPageContextDismissed
    }
  })

  const hasPageContextAttachment = useCallback((attachments?: AgentAttachment[]) => {
    if (!attachments || attachments.length === 0) return false
    return attachments.some(
      (a) => a.type === AgentAttachmentType.Context && (a as any)?.metadata?.kind === 'pageContext'
    )
  }, [])

  const queuePausedRef = useRef(false)
  const isProcessingQueueRef = useRef(false)
  const streamKeyRef = useRef<string | undefined>(undefined)
  const stopRequestedByChatRef = useRef<Set<string>>(new Set())

  const pendingKey = activeChatId ?? draftChatKey

  const generateTitle = useCallback(
    async (content: string, userId: string): Promise<string | undefined> => {
      if (!runtime.transport.generateTitle) return
      const strategy = runtime.config.features?.titleGeneration === 'llm' ? 'llm' : 'local'
      let title: string | undefined
      try {
        title = await runtime.transport.generateTitle({
          userId,
          content,
          strategy,
          contextKey
        })
      } catch {}
      return title
    },
    [runtime.transport, runtime.config.features?.titleGeneration, contextKey]
  )

  const sendContent = useCallback(
    async (content: string, _attachments: SendMessagePayload['attachments'] = []): Promise<SendResult> => {
      const userId = runtime.config.user?.id ?? 'guest'
      const isNewChat = !activeChatId
      const draftKey = activeChatId ?? draftChatKey
      const uiKeyRef = { current: draftKey }
      streamKeyRef.current = draftKey
      const draftBeforeSend = { value: content, attachments: _attachments }
      setComposerError(uiKeyRef.current, undefined)
      let metadata: SendMessagePayload['metadata']

      if (isNewChat) {
        const title = await generateTitle(content, userId)
        if (title && title.trim().length > 0) {
          metadata = { ...(metadata ?? {}), title }
        }
      }
      // Host-contributed metadata, re-evaluated on EVERY message (not only at
      // creation) so values that can change during a chat — e.g. an Office
      // docSessionId after a reconnect — stay current for the backend to read.
      // At chat creation it is also persisted as AgentChat.metadata; on later
      // messages it rides along in the payload (the backend does not re-persist
      // it, so it never clobbers the stored title or metadata).
      const chatMetadata = runtime.getChatMetadata({ contextKey })
      if (chatMetadata) {
        metadata = { ...(metadata ?? {}), ...chatMetadata }
      }

      const state = runtime.store.getState()
      const activeChat = activeChatId ? state.chats[activeChatId] : undefined
      const chatState = activeChatId ? state.chatState[activeChatId] : undefined
      const messages = chatState?.messages ?? []
      const attachments = _attachments.filter((a: AgentAttachment) => {
        if (!isPageContextAttachment(a)) return true
        return !hasSamePageContext(messages, a)
      })

      const chatModel = activeChat?.model
      const userChangedModel = composer.model !== chatModel

      let modelToUse: string | undefined
      let shouldUpdateChatModel = false

      if (userChangedModel) {
        modelToUse = composer.model
        shouldUpdateChatModel = true
      } else if (chatModel) {
        modelToUse = chatModel
        shouldUpdateChatModel = false
      } else {
        modelToUse = undefined
        shouldUpdateChatModel = false
      }

      // When switching to Auto (modelToUse undefined), we must still include model in the payload
      // so the backend enters its update block. JSON.stringify omits undefined; send empty string
      // so the backend can clear chat.model (payload.model || undefined).
      const mentions = extractMentions(content)
      const payload: SendMessagePayload | MultiUserMessagePayload = {
        userId,
        content,
        metadata,
        contextKey,
        ...(isNewChat ? {} : { chatId: activeChatId }),
        ...(shouldUpdateChatModel ? { model: modelToUse ?? '' } : {}),
        ...(attachments.length > 0 ? { attachments } : {}),
        ...(multiUser
          ? {
              mentions,
              participantIds: Array.from(new Set([userId, ...participantIds])),
              senderDisplayName: runtime.config.user?.displayName,
              senderAvatarUrl: runtime.config.user?.avatarUrl
            }
          : {})
      }

      const clearPending = () => {
        setPendingAssistant(uiKeyRef.current, undefined)
      }

      const handleError = (error: unknown): SendResult => {
        if ((error as any)?.name === 'AbortError') {
          const stopKey = streamKeyRef.current ?? uiKeyRef.current
          if (stopRequestedByChatRef.current.has(stopKey)) {
            // User pressed Stop: commit partial assistant output into chat history, then clear pending.
            // Note: uiKeyRef.current should already be switched to real chat id once StreamChunkType.Chat arrives.
            commitPendingAssistant(uiKeyRef.current)
            stopRequestedByChatRef.current.delete(stopKey)
          } else {
            // Unexpected abort: clear pending and restore input.
            clearPending()
            composerFormRef.current?.setDraft(draftBeforeSend)
          }
          return { type: 'aborted' }
        }
        throw error
      }

      const handleMultiUserPost = async (): Promise<SendResult> => {
        setChatStatus(uiKeyRef.current, UiChatStatus.Sending)
        try {
          if (!runtime.transport.sendMultiUserMessage) {
            throw new Error('Multi-user chat requires a transport that supports sendMultiUserMessage')
          }

          const result = await runtime.transport.sendMultiUserMessage(payload as MultiUserMessagePayload)
          upsertChat(result.chat)

          if (isNewChat) {
            moveChatKeyState(uiKeyRef.current, result.chat.id)
            uiKeyRef.current = result.chat.id
            streamKeyRef.current = result.chat.id
            setActiveChat(result.chat.id)
          }

          appendMessagesToChat(result.chat.id, [result.message])
          setChatStatus(result.chat.id, mapPersistedStatusToUiStatus(result.chat.status))
          clearPending()
          return { type: 'success' }
        } catch (err) {
          return handleError(err)
        }
      }

      const handleStreaming = async (): Promise<SendResult> => {
        let lastChunkId = `pending-${Date.now()}`
        let accumulatedReasoning = ''
        let lastAssistantMessage: (AgentMessage & { reasoning?: string }) | undefined

        setChatStatus(uiKeyRef.current, UiChatStatus.Streaming)
        setPendingAssistant(uiKeyRef.current, {
          id: lastChunkId,
          content: '',
          reasoning: '',
          toolCalls: [],
          attachments: [],
          chat: activeChatId || '',
          role: MessageRole.Assistant,
          createdAt: new Date().toISOString()
        })

        try {
          let currentChatId: string | undefined = activeChatId
          const handleChunk = (chunk: StreamChunk) => {
            if (chunk.type === StreamChunkType.Chat && chunk.chat) {
              upsertChat(chunk.chat)
              currentChatId = chunk.chat.id
              if (isNewChat) {
                // Switch UI to real chat id immediately.
                // Also switch cancel key to chat id (transports alias cancel to chat id).
                streamKeyRef.current = chunk.chat.id
                moveChatKeyState(uiKeyRef.current, chunk.chat.id)
                uiKeyRef.current = chunk.chat.id
                setActiveChat(chunk.chat.id)
              }
              return
            }
            if (chunk.type === StreamChunkType.Reasoning && chunk.reasoning) {
              accumulatedReasoning += chunk.reasoning
              const base: AgentMessage & { reasoning?: string } =
                lastAssistantMessage ??
                ({
                  id: lastChunkId,
                  chat: currentChatId,
                  role: MessageRole.Assistant,
                  content: '',
                  createdAt: new Date().toISOString()
                } as AgentMessage)

              lastAssistantMessage = { ...base, reasoning: accumulatedReasoning }
              setPendingAssistant(uiKeyRef.current, lastAssistantMessage)
              return
            }
            if (chunk.type === StreamChunkType.Error && chunk.error) {
              const errorContent =
                typeof chunk.error === 'string' ? chunk.error : String(chunk.error)
              const errorMessage: AgentMessage =
                lastAssistantMessage ??
                ({
                  id: lastChunkId,
                  chat: currentChatId || uiKeyRef.current,
                  role: MessageRole.Assistant,
                  content: errorContent,
                  createdAt: new Date().toISOString()
                } as AgentMessage)
              appendMessagesToChat(uiKeyRef.current, [{ ...errorMessage, content: errorContent }])
              setChatStatus(uiKeyRef.current, UiChatStatus.Idle)
              clearPending()
              return
            }
            if (chunk.message && chunk.message.role === MessageRole.User) {
              appendMessagesToChat(chunk.message.chat, [chunk.message])
              return
            }
            if (chunk.message) {
              lastChunkId = chunk.message.id
              lastAssistantMessage = {
                ...chunk.message,
                reasoning: accumulatedReasoning || (chunk.message as any).reasoning
              }
              setPendingAssistant(uiKeyRef.current, lastAssistantMessage)
            }

            if (chunk.done) {
              if (lastAssistantMessage) {
                appendMessagesToChat(uiKeyRef.current, [lastAssistantMessage])
              }
              setChatStatus(uiKeyRef.current, UiChatStatus.Idle)
              clearPending()
            }
          }

          const chat = await runtime.transport.streamMessage(
            payload as SendMessagePayload,
            handleChunk,
            { streamKey: streamKeyRef.current }
          )

          if (chat) {
            const messagesToPrepend = chat.messages.map((msg: AgentMessage) =>
              lastAssistantMessage && msg.id === lastAssistantMessage.id && msg.role === MessageRole.Assistant
                ? { ...msg, reasoning: accumulatedReasoning }
                : msg
            )
            appendMessagesToChat(chat.id, messagesToPrepend)
            const currentState = runtime.store.getState()
            const currentKey = currentState.activeChatId ?? currentState.draftChatKey
            if (currentKey === uiKeyRef.current) {
              setActiveChat(chat.id)
            }
            clearPending()
            return { type: 'success' }
          }
          clearPending()
          return { type: 'success' }
        } catch (err) {
          return handleError(err)
        }
      }

      try {
        const result = await (multiUser ? handleMultiUserPost() : handleStreaming())
        if (result.type === 'success') {
          queuePausedRef.current = false
        }
        return result
      } catch (err) {
        clearPending()
        const errorChat = (err as { chat?: AgentChat } | undefined)?.chat
        if (errorChat) {
          upsertChat(errorChat)
          setActiveChat(errorChat.id)
        }
        const normalizedError = err instanceof Error ? err : new Error('Failed to send message')
        setComposerError(uiKeyRef.current, normalizedError.message || 'Failed to send message')
        setChatStatus(uiKeyRef.current, UiChatStatus.Idle)
        composerFormRef.current?.setDraft(draftBeforeSend)
        queuePausedRef.current = false
        return { type: 'error', error: normalizedError }
      } finally {
        isProcessingQueueRef.current = false
        if (!multiUser) {
          setChatStatus(uiKeyRef.current, UiChatStatus.Idle)
        }
        streamKeyRef.current = undefined
      }
    },
    [
      activeChatId,
      contextKey,
      composer.model,
      multiUser,
      participantIds,
      runtime.transport,
      runtime.config.user?.displayName,
      runtime.config.user?.avatarUrl,
      runtime.config.user?.id,
      upsertChat,
      setActiveChat,
      setPendingAssistant,
      setChatStatus,
      generateTitle,
      initialSystemPrompt,
      moveChatKeyState,
      composerFormRef,
      setComposerError
    ]
  )

  const processNextQueuedMessage = useCallback(async () => {
    if (activeChatStatus !== UiChatStatus.Idle) return
    if (isProcessingQueueRef.current) return
    if (!queuedMessages.length) return
    if (queuePausedRef.current) return

    const next = queuedMessages[0]
    isProcessingQueueRef.current = true
    removeQueuedMessage(pendingKey, next.id)
    try {
      await sendContent(next.content, next.attachments)
    } finally {
      isProcessingQueueRef.current = false
    }
  }, [activeChatStatus, queuedMessages, pendingKey, sendContent, removeQueuedMessage])

  useEffect(() => {
    processNextQueuedMessage()
  }, [processNextQueuedMessage])

  const sendQueuedMessageById = useCallback(
    async (id: string) => {
      if (isProcessingQueueRef.current) return

      const message = queuedMessages.find((item) => item.id === id)
      if (!message) return

      queuePausedRef.current = false
      isProcessingQueueRef.current = true
      removeQueuedMessage(pendingKey, id)
      try {
        await sendContent(message.content, message.attachments)
      } finally {
        isProcessingQueueRef.current = false
      }
    },
    [queuedMessages, pendingKey, removeQueuedMessage, sendContent]
  )

  const handleSubmit = useCallback(
    async (content: string) => {
      const draft = composerFormRef.current.getDraft()
      const attachments = draft.attachments
      // if (hasPageContextAttachment(attachments)) {
      //   // Only include page context once per chat unless the user explicitly re-attaches it.
      //   setPageContextDismissed(pendingKey, true)
      // }
      if (!multiUser && activeChatStatus !== UiChatStatus.Idle) {
        enqueueQueuedMessage(pendingKey, content, attachments)
        composerFormRef.current?.clearDraft()
        return
      }

      queuePausedRef.current = false
      composerFormRef.current?.clearDraft()
      await sendContent(content, attachments)
    },
    [
      activeChatStatus,
      multiUser,
      enqueueQueuedMessage,
      pendingKey,
      sendContent,
      composerFormRef,
      hasPageContextAttachment,
      setPageContextDismissed
    ]
  )

  const handleStop = useCallback(() => {
    queuePausedRef.current = true
    const cancelKey = streamKeyRef.current ?? pendingKey
    stopRequestedByChatRef.current.add(cancelKey)
    runtime.transport.cancelStream?.(cancelKey)
    // Persist partial assistant output into the conversation and clear pending.
    // Commit under cancelKey when possible (it becomes the real chat id after StreamChunkType.Chat).
    commitPendingAssistant(cancelKey)
    setChatStatus(cancelKey, UiChatStatus.Idle)
  }, [pendingKey, runtime.transport, commitPendingAssistant, setChatStatus])

  const handleQueuedClick = useCallback(
    async (id: string) => {
      if (activeChatStatus === UiChatStatus.Streaming) {
        handleStop()
      }
      await sendQueuedMessageById(id)
    },
    [activeChatStatus, handleStop, sendQueuedMessageById]
  )

  return {
    pendingKey,
    queuedMessages,
    // Multi-user chats report the real status too: the server keeps chat.status
    // current over socket ChatUpdate events, and the composer needs it to offer
    // Stop while the agent (and its subagents) are running. Send is never
    // queue-blocked in multi-user mode, so surfacing the status only affects
    // the Stop affordance.
    activeChatStatus,
    activeChatId,
    removeQueuedMessage,
    handleSubmit,
    handleStop,
    handleQueuedClick
  }
}

const hasSamePageContext = (messages: AgentMessage[], attachment: AgentAttachment) => {
  const dataString = JSON.stringify(attachment.metadata?.data)
  let lastPageContextAttachment: AgentAttachment | undefined
  for (let i = messages.length - 1; i >= 0; i--) {
    const message = messages[i]
    const pageContextAttachment = message.attachments?.find(isPageContextAttachment)
    if (pageContextAttachment) {
      lastPageContextAttachment = pageContextAttachment
      break
    }
  }

  if (!lastPageContextAttachment || lastPageContextAttachment.url !== attachment.url) return false
  return JSON.stringify(lastPageContextAttachment.metadata?.data) === dataString
}

const isPageContextAttachment = (attachment: AgentAttachment) => {
  return attachment.type === AgentAttachmentType.Context && attachment.metadata?.kind === 'pageContext'
}

const extractMentions = (content: string): MultiUserMessagePayload['mentions'] => {
  const matches = content.matchAll(/(^|[\s([{])@([a-zA-Z0-9_.-]+)/g)
  const seen = new Set<string>()
  const mentions: NonNullable<MultiUserMessagePayload['mentions']> = []

  for (const match of matches) {
    const name = match[2]
    const key = name.toLowerCase()
    if (seen.has(key)) continue
    seen.add(key)
    mentions.push({
      type: key === 'agent' ? 'agent' : 'user',
      name
    })
  }

  return mentions.length > 0 ? mentions : undefined
}
